import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Paper,
  Chip,
} from '@mui/material';
import {
  Print as PrintIcon,
  Close as CloseIcon,
  Usb as UsbIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config';

const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: { 'Authorization': `Bearer ${token}` },
    withCredentials: true
  };
};

// Generate ZPL code with LARGER text sizes for 300dpi printer
// Header: 70pt, Labels: 50pt, Values: 58pt, Serial NO: 55pt, Serial Number: 160pt
const generateZPLClient = (data, serialNo, totalCartons) => {
  const product = (data.product || 'N/A').toUpperCase().substring(0, 20);
  const grade = (data.grade || 'N/A').toUpperCase().substring(0, 15);
  const packSize = (data.pack_size || 'N/A').toUpperCase().substring(0, 15);
  const lotNo = (data.lot_no || 'N/A').toUpperCase().substring(0, 20);
  const palletId = (data.pallet_id || '').toUpperCase().substring(0, 20);
  const date = data.date || new Date().toISOString().split('T')[0];
  const serialDisplay = `${serialNo}/${totalCartons}`;
  
  const dateObj = new Date(date);
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const dateFormatted = `${String(dateObj.getDate()).padStart(2, '0')}-${months[dateObj.getMonth()]}-${dateObj.getFullYear()}`;
  
  // QR Code data
  const qrData = `PRD:${product}|GRD:${grade}|PKS:${packSize}|LOT:${lotNo}|PLT:${palletId}|DT:${dateFormatted}|SRL:${serialNo}/${totalCartons}`;

  // 80mm = 944 dots at 300dpi
  let zpl = "^XA\n";
  zpl += "^MMT\n";           // Tear-off mode
  zpl += "^MTD\n";           // Direct thermal
  zpl += "^PW944\n";         // Print width 944 dots (80mm at 300dpi)
  zpl += "^LL944\n";         // Label length 944 dots
  zpl += "^LS0\n";           // Label shift
  zpl += "^LH0,0\n";         // Label home
  zpl += "^CI28\n";          // Unicode
  zpl += "^PR3,3,3\n";       // Print speed
  zpl += "^MD30\n";          // Media darkness
  
  // Outer border - thick
  zpl += "^FO15,15^GB914,914,4^FS\n";
  
  // Header - CARTON LABEL (70pt - large, bold, centered)
  zpl += "^FO30,25^A0N,70,70^FB884,1,0,C^FDCARTON LABEL^FS\n";
  zpl += "^FO15,105^GB914,0,3^FS\n";  // Header line
  
  // Row 1: PRODUCT (Labels: 50pt, Values: 58pt)
  zpl += "^FO30,120^A0N,50,48^FDPRODUCT:^FS\n";
  zpl += "^FO280,117^A0N,58,56^FD" + product + "^FS\n";
  zpl += "^FO15,185^GB914,0,2^FS\n";  // Divider
  
  // Row 2: GRADE
  zpl += "^FO30,200^A0N,50,48^FDGRADE:^FS\n";
  zpl += "^FO280,197^A0N,58,56^FD" + grade + "^FS\n";
  zpl += "^FO15,265^GB914,0,2^FS\n";  // Divider
  
  // Row 3: PACK SIZE
  zpl += "^FO30,280^A0N,50,48^FDPACK SIZE:^FS\n";
  zpl += "^FO280,277^A0N,58,56^FD" + packSize + "^FS\n";
  zpl += "^FO15,345^GB914,0,2^FS\n";  // Divider
  
  // Row 4: LOT NO
  zpl += "^FO30,360^A0N,50,48^FDLOT NO:^FS\n";
  zpl += "^FO280,357^A0N,58,56^FD" + lotNo + "^FS\n";
  zpl += "^FO15,425^GB914,0,2^FS\n";  // Divider
  
  // Row 5: PALLET ID
  zpl += "^FO30,440^A0N,50,48^FDPALLET ID:^FS\n";
  zpl += "^FO280,437^A0N,58,56^FD" + (palletId || '-') + "^FS\n";
  zpl += "^FO15,505^GB914,0,2^FS\n";  // Divider
  
  // Row 6: DATE
  zpl += "^FO30,520^A0N,50,48^FDDATE:^FS\n";
  zpl += "^FO280,517^A0N,58,56^FD" + dateFormatted + "^FS\n";
  zpl += "^FO15,590^GB914,0,4^FS\n";  // Thick divider before serial
  
  // SERIAL NO Section - Left side (55pt label, 160pt number)
  zpl += "^FO30,610^A0N,55,55^FDSERIAL NO^FS\n";
  zpl += "^FO30,680^A0N,160,160^FD" + serialDisplay + "^FS\n";
  
  // QR Code - Right side (smaller, magnification 5)
  zpl += "^FO680,610^BQN,2,5^FDQA," + qrData + "^FS\n";
  
  // Bottom text
  zpl += "^FO680,850^A0N,28,28^FDScan QR^FS\n";
  
  // Bottom line
  zpl += "^FO15,890^GB914,0,2^FS\n";
  
  // Lot-Serial at bottom
  zpl += "^FO30,900^A0N,35,35^FD" + lotNo + "-" + String(serialNo).padStart(3, '0') + "^FS\n";
  
  zpl += "^XZ\n";
  
  return zpl;
};

function LabelPrintDialog({ open, onClose, labelData }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [printStatus, setPrintStatus] = useState(null);
  
  const [formData, setFormData] = useState({
    product: '',
    grade: '',
    pack_size: '',
    lot_no: '',
    pallet_id: '',
    date: new Date().toISOString().split('T')[0],
    total_cartons: 1,
    inventory_id: null,
  });

  useEffect(() => {
    if (labelData) {
      setFormData({
        product: labelData.product || '',
        grade: labelData.grade || '',
        pack_size: labelData.pack_size || '',
        lot_no: labelData.lot_no || '',
        pallet_id: labelData.pallet_id || '',
        date: labelData.production_date || new Date().toISOString().split('T')[0],
        total_cartons: labelData.cartons || 1,
        inventory_id: labelData.inventory_id || null,
      });
    }
  }, [labelData]);

  useEffect(() => {
    if (!open) {
      setError('');
      setSuccess('');
      setPrintStatus(null);
    }
  }, [open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
    setSuccess('');
  };

  // Open popup with ZPL and print instructions
  const openPrintWindow = (zplContent, labelCount) => {
    const printWindow = window.open('', '_blank', 'width=700,height=700');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print ${labelCount} Labels - ZEBRA ZD421</title>
        <style>
          * { box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 20px; margin: 0; background: #f5f5f5; }
          .header { background: #1976d2; color: white; padding: 15px 20px; border-radius: 8px; margin-bottom: 20px; }
          .header h2 { margin: 0; font-size: 1.3rem; }
          .card { background: white; padding: 20px; border-radius: 8px; margin-bottom: 15px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
          .btn { padding: 12px 24px; border: none; border-radius: 6px; cursor: pointer; font-size: 14px; margin: 5px; font-weight: 600; }
          .btn-primary { background: #1976d2; color: white; }
          .btn-success { background: #4caf50; color: white; }
          .btn:hover { opacity: 0.9; }
          .zpl-box { background: #1e1e1e; color: #9cdcfe; padding: 15px; border-radius: 6px; font-family: monospace; font-size: 11px; max-height: 150px; overflow: auto; white-space: pre; }
          .success { background: #e8f5e9; border: 2px solid #4caf50; padding: 12px 15px; border-radius: 6px; color: #2e7d32; display: none; margin-top: 15px; }
          .info-box { background: #e3f2fd; border-left: 4px solid #1976d2; padding: 12px 15px; margin: 15px 0; }
          h3 { color: #333; margin-top: 0; }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>🖨️ Print ${labelCount} Carton Label${labelCount > 1 ? 's' : ''}</h2>
          <p style="margin:5px 0 0 0;opacity:0.9;font-size:13px">ZDesigner ZD421-300dpi ZPL | USB001</p>
        </div>
        
        <div class="card">
          <h3>⚡ Quick Actions</h3>
          <button class="btn btn-primary" onclick="copyZPL()">📋 Copy ZPL Code</button>
          <button class="btn btn-success" onclick="downloadZPL()">💾 Download .zpl File</button>
          <div id="copySuccess" class="success">✅ Copied! Paste in Zebra Direct Communication window</div>
        </div>

        <div class="card">
          <h3>📋 How to Print</h3>
          <div class="info-box">
            <strong>Step 1:</strong> Click "Copy ZPL Code" above<br>
            <strong>Step 2:</strong> Open Zebra Setup Utilities<br>
            <strong>Step 3:</strong> Select your printer → Click "Open Communication With Printer"<br>
            <strong>Step 4:</strong> Paste the code → Click Send<br>
            <strong>Done!</strong> Labels will print 🎉
          </div>
        </div>

        <div class="card">
          <h3>📄 ZPL Code Preview</h3>
          <div class="zpl-box">${zplContent.substring(0, 500)}...</div>
        </div>

        <script>
          const fullZPL = ${JSON.stringify(zplContent)};
          
          function copyZPL() {
            navigator.clipboard.writeText(fullZPL).then(() => {
              document.getElementById('copySuccess').style.display = 'block';
            }).catch(() => {
              const ta = document.createElement('textarea');
              ta.value = fullZPL;
              document.body.appendChild(ta);
              ta.select();
              document.execCommand('copy');
              document.body.removeChild(ta);
              document.getElementById('copySuccess').style.display = 'block';
            });
          }
          
          function downloadZPL() {
            const blob = new Blob([fullZPL], { type: 'application/octet-stream' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = 'labels_${formData.lot_no}_${formData.total_cartons}.zpl';
            a.click();
          }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handlePrint = async () => {
    if (!formData.product) { setError('Product is required'); return; }
    if (!formData.lot_no) { setError('Lot No is required'); return; }
    if (!formData.total_cartons || formData.total_cartons < 1) { setError('Total cartons must be at least 1'); return; }

    setLoading(true);
    setError('');
    setSuccess('');
    setPrintStatus('sending');

    try {
      let allZpl = '';
      for (let i = 1; i <= formData.total_cartons; i++) {
        allZpl += generateZPLClient(formData, i, formData.total_cartons);
      }

      // Log to server
      try {
        await axios.post(API_ENDPOINTS.PRINT_LABELS, { action: 'print', ...formData }, getAuthConfig());
      } catch (e) { console.log('Server logging skipped'); }

      // Open popup with ZPL
      openPrintWindow(allZpl, formData.total_cartons);
      setPrintStatus('success');
      setSuccess(`✅ ${formData.total_cartons} labels generated! Check the popup window.`);

    } catch (err) {
      setPrintStatus('error');
      setError(err.message || 'Failed to generate labels');
    } finally {
      setLoading(false);
    }
  };

  const handleTestPrint = async () => {
    setLoading(true);
    setError('');

    try {
      const testZpl = generateZPLClient({
        product: 'TEST',
        grade: 'TEST',
        pack_size: '1X10 KG',
        lot_no: 'TEST-001',
        pallet_id: 'PLT-001',
        date: new Date().toISOString().split('T')[0],
      }, 1, 1);

      openPrintWindow(testZpl, 1);
      setSuccess('✅ Test label generated!');
    } catch (err) {
      setError('Failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PrintIcon /> Print Carton Labels
        </Box>
        <Chip 
          icon={<UsbIcon sx={{ color: 'white !important' }} />} 
          label="ZD421-300dpi" 
          size="small" 
          sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} 
        />
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Label:</strong> 80mm × 80mm (944×944 dots) | <strong>Printer:</strong> ZD421-300dpi (USB001)
        </Alert>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>📋 Label Information</Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Product *" value={formData.product} onChange={(e) => handleChange('product', e.target.value)} InputProps={{ readOnly: !!labelData?.product }} sx={{ '& input': { fontWeight: 600 } }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Grade" value={formData.grade} onChange={(e) => handleChange('grade', e.target.value)} InputProps={{ readOnly: !!labelData?.grade }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Pack Size" value={formData.pack_size} onChange={(e) => handleChange('pack_size', e.target.value)} InputProps={{ readOnly: !!labelData?.pack_size }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Lot No *" value={formData.lot_no} onChange={(e) => handleChange('lot_no', e.target.value)} InputProps={{ readOnly: !!labelData?.lot_no }} sx={{ '& input': { fontWeight: 600 } }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Pallet ID" value={formData.pallet_id} onChange={(e) => handleChange('pallet_id', e.target.value)} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth type="date" label="Date" value={formData.date} onChange={(e) => handleChange('date', e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth type="number" label="Total Cartons *" value={formData.total_cartons} onChange={(e) => handleChange('total_cartons', parseInt(e.target.value) || 1)} inputProps={{ min: 1, max: 999 }} helperText="1/N → N/N" sx={{ '& input': { fontWeight: 600, fontSize: '1.1rem', textAlign: 'center' } }} />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>👁️ Label Preview</Typography>
            <Paper elevation={3} sx={{ p: 1.5, bgcolor: '#f5f5f5', border: '2px dashed #ccc' }}>
              <Box sx={{ maxWidth: 220, mx: 'auto', bgcolor: 'white', border: '2px solid #333', borderRadius: 1, p: 1, fontFamily: 'Arial' }}>
                <Typography align="center" sx={{ fontWeight: 800, borderBottom: '2px solid #333', pb: 0.5, fontSize: '0.85rem' }}>CARTON LABEL</Typography>
                <Box sx={{ fontSize: '0.6rem', lineHeight: 1.8, mt: 0.5 }}>
                  {[
                    { label: 'PRODUCT:', value: formData.product },
                    { label: 'GRADE:', value: formData.grade },
                    { label: 'PACK SIZE:', value: formData.pack_size },
                    { label: 'LOT NO:', value: formData.lot_no },
                    { label: 'PALLET ID:', value: formData.pallet_id },
                    { label: 'DATE:', value: formData.date ? new Date(formData.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase() : '-' },
                  ].map((item, idx) => (
                    <Box key={idx} sx={{ display: 'flex', borderBottom: '1px solid #ddd', py: 0.2 }}>
                      <Typography sx={{ width: 70, fontWeight: 700, fontSize: '0.55rem' }}>{item.label}</Typography>
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 600 }}>{item.value || '-'}</Typography>
                    </Box>
                  ))}
                  <Box sx={{ borderTop: '2px solid #333', mt: 0.5, pt: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.55rem' }}>SERIAL NO</Typography>
                      <Typography sx={{ fontWeight: 900, fontSize: '1.3rem', color: '#1976d2', lineHeight: 1 }}>1/{formData.total_cartons}</Typography>
                    </Box>
                    <Box sx={{ width: 45, height: 45, border: '1px solid #333', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.4rem', borderRadius: 0.5 }}>
                      QR
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {printStatus === 'sending' && (
          <Box sx={{ mt: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
        <Button onClick={handleTestPrint} disabled={loading} variant="outlined" color="secondary" size="small">Test Print</Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={() => onClose()} disabled={loading} startIcon={<CloseIcon />}>Cancel</Button>
        <Button 
          onClick={handlePrint} 
          variant="contained" 
          disabled={loading || !formData.product || !formData.lot_no || !formData.total_cartons} 
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PrintIcon />} 
          sx={{ minWidth: 160 }}
        >
          {loading ? 'Generating...' : `Print ${formData.total_cartons} Labels`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default LabelPrintDialog;